import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { MagnifyingGlassIcon, FlagIcon, XMarkIcon } from '@heroicons/react/24/outline'
import api from '../../services/api'

interface Complaint {
  id: number
  reference_number: string
  subject: string
  category: string
  priority: string
  status: string
  name: string
  email: string
  description: string
  resolution?: string
  created_at: string
  resolved_at?: string
}

const PRIORITY_COLORS: Record<string, string> = {
  low:    'bg-dark-700 text-gray-400',
  medium: 'bg-blue-500/10 text-blue-400',
  high:   'bg-orange-500/10 text-orange-400',
  urgent: 'bg-red-500/10 text-red-400',
}

const STATUS_COLORS: Record<string, string> = {
  open:        'bg-red-500/10 text-red-400',
  in_progress: 'bg-blue-500/10 text-blue-400',
  resolved:    'bg-green-500/10 text-green-400',
  closed:      'bg-dark-700 text-gray-400',
}

interface ActionModalProps {
  complaint: Complaint
  onClose: () => void
  onAction: () => void
}

function ActionModal({ complaint, onClose, onAction }: ActionModalProps) {
  const [status, setStatus] = useState(complaint.status)
  const [resolution, setResolution] = useState(complaint.resolution || '')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setLoading(true)
    setError('')
    try {
      await api.put(`/admin/complaints/${complaint.id}`, { status, resolution, internal_notes: notes })
      onAction()
      onClose()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Update failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-dark-800 rounded-2xl shadow-xl border border-dark-700 w-full max-w-md p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-bold text-white">Grievance #{complaint.reference_number}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{complaint.name} · {complaint.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <div className="bg-dark-900 rounded-xl p-4 mb-4 border border-dark-700">
          <p className="text-xs font-medium text-gray-300 mb-1">{complaint.subject}</p>
          <p className="text-xs text-gray-400 leading-relaxed">{complaint.description}</p>
        </div>

        {error && <p className="text-sm text-red-400 mb-3">{error}</p>}

        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="w-full text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Resolution (for client)</label>
            <textarea rows={2} value={resolution} onChange={e => setResolution(e.target.value)}
              className="w-full text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder="Resolution communicated to client…" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Internal Notes</label>
            <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder="Internal notes (not shown to client)…" />
          </div>
        </div>

        <button onClick={submit} disabled={loading}
          className="w-full py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors">
          {loading ? 'Updating…' : 'Update Grievance'}
        </button>
      </div>
    </div>
  )
}

export default function GrievancesPage() {
  const [complaints, setComplaints] = useState<Complaint[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selected, setSelected] = useState<Complaint | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { limit: 100 }
      if (statusFilter) params.status = statusFilter
      const res = await api.get('/admin/complaints', { params })
      const all: Complaint[] = res.data.complaints
      setTotal(res.data.total)
      setComplaints(search ? all.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.reference_number.toLowerCase().includes(search.toLowerCase()) ||
        c.subject.toLowerCase().includes(search.toLowerCase())
      ) : all)
    } catch {}
    finally { setLoading(false) }
  }, [search, statusFilter])

  useEffect(() => { fetch() }, [fetch])

  const open = complaints.filter(c => c.status === 'open').length
  const inProgress = complaints.filter(c => c.status === 'in_progress').length
  const resolved = complaints.filter(c => c.status === 'resolved').length

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Grievances</nav>
          <h1 className="text-2xl font-bold text-white">SCORES Grievances</h1>
          <p className="text-sm text-gray-400 mt-0.5">Client complaints — SEBI SLA: resolve within 21 days</p>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3">
        {[
          { label: 'Total', count: total, color: 'bg-dark-700 text-gray-300' },
          { label: 'Open', count: open, color: 'bg-red-500/10 text-red-400' },
          { label: 'In Progress', count: inProgress, color: 'bg-blue-500/10 text-blue-400' },
          { label: 'Resolved', count: resolved, color: 'bg-green-500/10 text-green-400' },
        ].map(s => (
          <div key={s.label} className={`px-4 py-2 rounded-xl text-sm font-medium ${s.color}`}>
            {s.label}: <strong>{s.count}</strong>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ width: 16, height: 16 }} />
          <input
            type="text"
            placeholder="Search by name, reference, subject…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700 bg-dark-900">
                {['Ref #', 'Client', 'Subject', 'Category', 'Priority', 'Status', 'Filed', 'Action'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">Loading…</td></tr>
              ) : complaints.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <FlagIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No grievances found</p>
                  </td>
                </tr>
              ) : complaints.map(c => (
                <tr key={c.id} className="border-b border-dark-700 hover:bg-dark-700 transition-colors">
                  <td className="px-4 py-3 text-xs font-mono text-gray-400 whitespace-nowrap">{c.reference_number}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-100 text-sm">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.email}</p>
                  </td>
                  <td className="px-4 py-3 max-w-48">
                    <p className="text-sm text-gray-300 truncate">{c.subject}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 bg-dark-700 text-gray-400 rounded-full capitalize">{c.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PRIORITY_COLORS[c.priority] || 'bg-dark-700 text-gray-400'}`}>
                      {c.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status] || 'bg-dark-700 text-gray-400'}`}>
                      {c.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setSelected(c)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-dark-600 text-gray-400 hover:bg-amber-500/10 hover:border-amber-500/30 hover:text-amber-400 transition-colors">
                      Respond
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <ActionModal complaint={selected} onClose={() => setSelected(null)} onAction={fetch} />
      )}
    </div>
  )
}
